import React, { useContext, useEffect, useState } from 'react';
import { SelectedCompanyContext } from '../contexts/SelectedCompanyContext';
import api from '../api';
import { useParams } from 'react-router-dom';
import {
  AnimatedBackground,
  ModernCard,
  ModernButton,
} from './ui/modern';

const REQUIRED_FIELDS = ['serverName', 'ipAddress', 'username', 'password', 'domain'];

const OnboardingServerInfo = () => {
  const { selected, setSelected } = useContext(SelectedCompanyContext);
  const { companyId } = useParams();
  const [formData, setFormData] = useState({ hasServers: false });
  const [servers, setServers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [wizardSaving, setWizardSaving] = useState(false);
  const [wizardError, setWizardError] = useState('');
  const [wizardMode, setWizardMode] = useState('create'); // 'create' | 'view'
  const [selectedServerId, setSelectedServerId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [wizardForm, setWizardForm] = useState({
    serverName: '',
    ipAddress: '',
    username: '',
    password: '',
    domain: '',
    serverLocation: '',
    branchId: '',
    serverRoles: '',
    notes: '',
  });

  useEffect(() => {
    if (selected) {
      setFormData({
        hasServers: !!selected.hasServers,
      });
    }
  }, [selected]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await api.get('/auth/verify');
        if (res.data?.valid) setCurrentUser(res.data.user);
      } catch (e) {
        console.error('auth verify', e);
      }
    };
    loadUser();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await api.put(`/companies/${companyId}`, formData);
      // Preserve existing tasks so we don't trigger onboarding redirects
      const merged = {
        ...selected,
        ...res.data,
        CompanyTasks: selected?.CompanyTasks || []
      };
      setSelected(merged);
      setMessage('Server info saved');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // fetch servers list
  useEffect(() => {
    const fetchServers = async () => {
      if (!selected || !selected.hasServers) return;
      try {
        const list = await api.get(`/companies/${companyId}/servers`).then(r => r.data);
        setServers(list);
      } catch (e) {
        console.error('fetch servers', e);
      }
    };
    fetchServers();
  }, [selected, companyId]);

  // fetch branches (for per-branch server placement)
  useEffect(() => {
    const fetchBranches = async () => {
      if (!selected) return;
      try {
        const list = await api.get(`/companies/${companyId}/branches`).then(r => r.data);
        setBranches(list);
      } catch (e) {
        console.error('fetch branches', e);
      }
    };
    fetchBranches();
  }, [selected, companyId]);

  const updateServerField = async (srv, changes) => {
    try {
      const res = await api.put(`/companies/server/${srv.id}`, changes);
      setServers(prev => prev.map(s => s.id === srv.id ? res.data : s));
    } catch (e) {
      console.error('update server', e);
    }
  };

  const handleSaveServer = (srv) => {
    if (srv.completed) return;
    for (const f of REQUIRED_FIELDS) {
      if (!srv[f]) return alert('Fill ' + f);
    }
    updateServerField(srv, { completed: true });
  };

  const handleUnlockServer = (srv, event) => {
    if (event?.stopPropagation) event.stopPropagation();
    updateServerField(srv, { completed: false, completedAt: null, completedById: null });
  };

  const resetWizard = () => {
    setSelectedServerId(null);
    setWizardMode('create');
    setShowPassword(false);
    setWizardForm({
      serverName: '',
      ipAddress: '',
      username: '',
      password: '',
      domain: '',
      serverLocation: '',
      branchId: '',
      serverRoles: '',
      notes: '',
    });
    setWizardError('');
  };

  const openWizard = () => {
    resetWizard();
    setShowWizard(true);
  };

  const openWizardForServer = (srv) => {
    setWizardMode('view');
    setSelectedServerId(srv.id);
    setShowPassword(false);
    setWizardForm({
      serverName: srv.serverName || '',
      ipAddress: srv.ipAddress || '',
      username: srv.username || '',
      password: srv.password || '',
      domain: srv.domain || '',
      serverLocation: srv.branchId ? 'Per Branch' : (srv.location || ''),
      branchId: srv.branchId ? String(srv.branchId) : '',
      serverRoles: srv.serverRoles || '',
      notes: srv.notes || '',
    });
    setShowWizard(true);
  };

  const closeWizard = () => {
    setShowWizard(false);
    setWizardSaving(false);
    setWizardError('');
  };

  const handleWizardSubmit = async () => {
    setWizardError('');
    if (!wizardForm.serverLocation) {
      setWizardError('Select server location');
      return;
    }
    for (const f of REQUIRED_FIELDS) {
      if (!wizardForm[f]) {
        setWizardError('Please fill ' + f);
        return;
      }
    }
    if (wizardForm.serverLocation === 'Per Branch' && !wizardForm.branchId) {
      setWizardError('Select a branch for this server');
      return;
    }
    const payload = {
      serverName: wizardForm.serverName,
      ipAddress: wizardForm.ipAddress,
      username: wizardForm.username,
      password: wizardForm.password,
      domain: wizardForm.domain,
      serverRoles: wizardForm.serverRoles,
      notes: wizardForm.notes,
      location: wizardForm.serverLocation === 'Per Branch' ? '' : wizardForm.serverLocation,
      branchId: wizardForm.serverLocation === 'Per Branch' && wizardForm.branchId
        ? parseInt(wizardForm.branchId, 10)
        : null,
    };
    try {
      setWizardSaving(true);
    if (wizardMode === 'view' && selectedServerId) {
      // view-only; no submission
      return;
    }
    if (wizardMode === 'create') {
      if (wizardForm.serverLocation === 'Per Branch' && payload.branchId) {
        await api.post(`/companies/branch/${payload.branchId}/servers`, payload);
      } else {
        await api.post(`/companies/${companyId}/servers`, payload);
      }
    }
      const refreshed = await api.get(`/companies/${companyId}/servers`).then(r => r.data);
      setServers(refreshed);
      closeWizard();
    } catch (e) {
      console.error('create server', e);
      setWizardError('Failed to save server');
    } finally {
      setWizardSaving(false);
    }
  };

  const handleDeleteServer = async (srv) => {
    try {
      if (srv.branchId) {
        await api.delete(`/companies/branch/${srv.branchId}/servers/${srv.id}`);
      } else {
        await api.delete(`/companies/server/${srv.id}`);
      }
      const refreshed = await api.get(`/companies/${companyId}/servers`).then(r => r.data);
      setServers(refreshed);
    } catch (e) {
      console.error('delete server', e);
      setWizardError('Failed to delete server');
    }
  };

  if (!selected) return null;

  const isEditable = true;
  const isAdmin = currentUser?.role === 'Admin';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <AnimatedBackground />

      <div className="relative z-10 p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <ModernCard className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <span>🖥️</span>
            Server Information
          </h1>
          <p className="text-slate-400">Configure server infrastructure and credentials</p>
        </ModernCard>

        {/* Messages */}
        {error && (
          <ModernCard className="mb-6 border-red-500/50 bg-red-500/10">
            <p className="text-red-400">{error}</p>
          </ModernCard>
        )}
        {message && (
          <ModernCard className="mb-6 border-emerald-500/50 bg-emerald-500/10">
            <p className="text-emerald-400">{message}</p>
          </ModernCard>
        )}

        {/* Server Configuration */}
        <ModernCard className="mb-6">
          <h3 className="text-xl font-bold text-white mb-6">Server Configuration</h3>

          {/* Has Servers */}
          <div className="mb-6">
            <label className="block text-slate-300 font-medium mb-3">
              Does the client have servers?
            </label>
            {isEditable ? (
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="hasServers"
                    value="yes"
                    checked={formData.hasServers}
                    onChange={() => setFormData({ ...formData, hasServers: true })}
                    className="w-4 h-4"
                  />
                  <span className="text-white">Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="hasServers"
                    value="no"
                    checked={!formData.hasServers}
                    onChange={() => setFormData({ ...formData, hasServers: false })}
                    className="w-4 h-4"
                  />
                  <span className="text-white">No</span>
                </label>
              </div>
            ) : (
              <p className="text-white">{selected.hasServers ? 'Yes' : 'No'}</p>
            )}
          </div>

          {/* Save Button */}
          {isEditable && (
            <div className="mt-6">
              <ModernButton onClick={handleSave} disabled={saving} variant="primary">
                {saving ? 'Saving...' : 'Save Configuration'}
              </ModernButton>
            </div>
          )}
        </ModernCard>

        {/* Server Add Wizard Trigger */}
        {selected && formData.hasServers && (
          <ModernCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Servers</h3>
              <ModernButton variant="primary" onClick={openWizard}>
                + Add Server
              </ModernButton>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Capture each server via the wizard. When “Per Branch” is selected, you must choose a branch for the server.
            </p>

            {servers.length === 0 && (
              <div className="text-slate-400 text-sm bg-white/5 border border-white/10 rounded-lg px-4 py-6 text-center">
                No servers yet. Click “Add Server” to create one.
              </div>
            )}

                {servers.length > 0 && (
                  <div className="grid gap-4 md:grid-cols-2">
                    {servers.map((srv) => (
                      <div
                        key={srv.id}
                        className="rounded-xl border border-white/10 bg-white/5 p-4 flex flex-col gap-3 hover:border-blue-500/50 transition cursor-pointer"
                        onClick={() => openWizardForServer(srv)}
                      >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-white font-semibold text-lg">{srv.serverName || 'Unnamed server'}</div>
                        <div className="text-slate-400 text-sm">{srv.ipAddress || 'No IP set'}</div>
                      </div>
                      <div className={`text-xs px-2 py-1 rounded-full ${srv.completed ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-200'}`}>
                        {srv.completed ? 'Saved' : 'Not saved'}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm text-slate-300">
                      <div>
                        <div className="text-slate-500 text-xs">Username</div>
                        <div>{srv.username || '—'}</div>
                      </div>
                      <div>
                        <div className="text-slate-500 text-xs">Domain</div>
                        <div>{srv.domain || '—'}</div>
                      </div>
                      <div>
                        <div className="text-slate-500 text-xs">Location</div>
                        <div>
                          {srv.branchId
                            ? branches.find(b => b.id === srv.branchId)?.name || 'Branch'
                            : srv.location || '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-500 text-xs">Roles</div>
                        <div className="truncate">{srv.serverRoles || '—'}</div>
                      </div>
                    </div>

                    <div className="text-slate-400 text-sm">
                      <div className="text-slate-500 text-xs mb-1">Notes</div>
                      <div className="min-h-[32px]">
                        {srv.notes || '—'}
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      {!srv.completed && (
                        <ModernButton
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteServer(srv)}
                        >
                          Delete
                        </ModernButton>
                      )}
                      {!srv.completed && (
                        <ModernButton
                          variant="primary"
                          size="sm"
                          onClick={() => handleSaveServer(srv)}
                        >
                          Save
                        </ModernButton>
                      )}
                      {srv.completed && isAdmin && (
                        <ModernButton
                          variant="glass"
                          size="sm"
                          onClick={(e) => handleUnlockServer(srv, e)}
                        >
                          Unlock
                        </ModernButton>
                      )}
                      {srv.completed && (
                        <div className="text-emerald-300 text-xs">
                          ✓ {srv.completedAt ? new Date(srv.completedAt).toLocaleString() : ''}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ModernCard>
        )}

        {/* Add Server Wizard Modal */}
        {showWizard && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="bg-slate-900 w-full max-w-2xl rounded-2xl border border-white/10 shadow-2xl">
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-white">Add Server</h3>
                  <p className="text-slate-400 text-sm">Capture server details and location</p>
                </div>
                <button
                  onClick={closeWizard}
                  className="text-slate-400 hover:text-white text-xl"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="p-6 space-y-4">
                {wizardError && (
                  <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/40 rounded-lg px-3 py-2">
                    {wizardError}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Server Name</label>
                    <input
                      value={wizardForm.serverName}
                      onChange={e => setWizardForm({ ...wizardForm, serverName: e.target.value })}
                      disabled={wizardMode === 'view'}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      placeholder="e.g., DC01"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">IP Address</label>
                    <input
                      value={wizardForm.ipAddress}
                      onChange={e => setWizardForm({ ...wizardForm, ipAddress: e.target.value })}
                      disabled={wizardMode === 'view'}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      placeholder="e.g., 10.0.0.10"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Username</label>
                    <input
                      value={wizardForm.username}
                      onChange={e => setWizardForm({ ...wizardForm, username: e.target.value })}
                      disabled={wizardMode === 'view'}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      placeholder="e.g., admin"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Password</label>
                    <div className="flex items-center gap-2">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={wizardForm.password}
                        onChange={e => setWizardForm({ ...wizardForm, password: e.target.value })}
                        disabled={wizardMode === 'view'}
                        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-200 hover:bg-white/10 disabled:opacity-50"
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Domain</label>
                    <input
                      value={wizardForm.domain}
                      onChange={e => setWizardForm({ ...wizardForm, domain: e.target.value })}
                      disabled={wizardMode === 'view'}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      placeholder="e.g., corp.local"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Location</label>
                    <select
                      value={wizardForm.serverLocation}
                      onChange={e => setWizardForm({ ...wizardForm, serverLocation: e.target.value, branchId: '' })}
                      disabled={wizardMode === 'view'}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    >
                      <option value="">Select location</option>
                      <option value="Head Office">Head Office</option>
                      <option value="Cloud Based">Cloud Based</option>
                      <option value="Per Branch">Per Branch</option>
                    </select>
                  </div>
                  {wizardForm.serverLocation === 'Per Branch' && (
                    <div>
                      <label className="block text-slate-300 text-sm mb-1">Branch</label>
                      <select
                        value={wizardForm.branchId}
                        onChange={e => setWizardForm({ ...wizardForm, branchId: e.target.value })}
                        disabled={wizardMode === 'view'}
                        className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      >
                        <option value="">Select branch</option>
                        {branches.map(b => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <label className="block text-slate-300 text-sm mb-1">Server Roles</label>
                    <textarea
                      value={wizardForm.serverRoles}
                      onChange={e => setWizardForm({ ...wizardForm, serverRoles: e.target.value })}
                      disabled={wizardMode === 'view'}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
                      rows={2}
                      placeholder="e.g., AD DS, File services"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-slate-300 text-sm mb-1">Notes</label>
                    <textarea
                      value={wizardForm.notes}
                      onChange={e => setWizardForm({ ...wizardForm, notes: e.target.value })}
                      disabled={wizardMode === 'view'}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
                      rows={2}
                      placeholder="Additional details"
                    />
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex items-center justify-end gap-3">
                <ModernButton variant="secondary" onClick={closeWizard} disabled={wizardSaving}>
                  Close
                </ModernButton>
                {wizardMode === 'create' && (
                  <ModernButton variant="primary" onClick={handleWizardSubmit} disabled={wizardSaving}>
                    {wizardSaving ? 'Saving...' : 'Save Server'}
                  </ModernButton>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingServerInfo;
